import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Radio,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import CrmMemberAvatar from "../org/CrmMemberAvatar";
import Label from "../../../Label";
import useAuth from "../../../../hooks/useAuth";
import useOrgDirectory from "../../../../hooks/useOrgDirectory";
import useWebotServer from "../../../../hooks/useWebotServer";
import { normalizeCrmOwnerId, sameCrmOwner } from "../crmRowAccess";
import { formatContactPhone, relativeHebrew } from "../crmFormat";
import type { CrmContactHolder, OrgDirectoryMember } from "../../../../@types/webotServer";
import type { REST_schema } from "../../../../@types/sharedSchema";
import type { CrmHandlerView } from "./crmHandlerFace";

/**
 * SET (OR CLEAR) THE מתווך מטפל FOR ONE PERSON.
 *
 * Two steps, and the second only exists when it has a question to ask:
 *
 *   A — WHO. The agency roster, single-select, with a per-row hint that says what will
 *       happen: `מטפל כעת` / `שמור אצלו` (no copy needed) / `יווצר עותק חדש`.
 *   B — WHICH RECORD TO COPY FROM. Rendered ONLY when the chosen agent holds no copy AND
 *       more than one colleague does. One holder → skipped, and the note names them.
 *       Assignee already holds it → skipped, and the consequence line says so.
 *
 * ── THE CONSEQUENCE SENTENCE IS THE MOST IMPORTANT STRING HERE ─────────────────
 *
 * A copy is a SNAPSHOT. From the moment it lands the two records are independent, exactly
 * like every other pair of copies in the product — and the satellites (calls, meetings,
 * documents, notes) are keyed by (owner, contact) and do NOT travel, so the assignee's card
 * starts with a clean history. Nobody has ever been told either of those things, because
 * until now copies only ever appeared by accident. The dialog says both, in every branch,
 * before the button that does it.
 *
 * ── NO UNDO, DELIBERATELY ──────────────────────────────────────────────────────
 *
 * A copy now exists in a colleague's book. An "undo" that silently deletes a document from
 * somebody else's account is a worse action than the one it reverses. Re-designating is one
 * click away, and the success toast offers a way to go and look.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  contactId: string;
  /** For the dialog's subtitle — the row already knows it, so no lookup for a title. */
  displayName?: string | null;
  /** The agency this designation belongs to. Required: a designation is per (org, person). */
  orgId: string;
  /** `null` = nobody is designated yet. */
  handler: CrmHandlerView | null;
  /** Repaints the badge from the response before any refetch. */
  onAssigned: (assignment: REST_schema.CrmContactAssignment | null) => void;
};

/** Sorted admins-first then by name — the order `useOrgDirectory` already hands out. */
export default function CrmAssignHandlerDialog({ open, onClose, contactId, displayName, orgId, handler, onAssigned }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // `getMember` (faces) comes from the session-wide union — a colleague's photo is their
  // photo whichever agency you share with them. The ROSTER does not; see `orgMembers` below.
  const { getMember } = useOrgDirectory();
  const { contactsHolders, crmAssignContactHandler, orgDirectory } = useWebotServer();
  const { user } = useAuth();
  const viewerUserId = (user?.email as string | undefined) ?? null;

  /**
   * THE ROSTER OF **THIS** AGENCY — not the session directory.
   *
   * ⚠️ `useOrgDirectory` RETURNS THE UNION ACROSS EVERY AGENCY THE CALLER IS IN, and says so
   * in its header: it deliberately takes no `orgId`, so that its cache is single-keyed and
   * "one fetch per session" is literally true. That is right for rendering a face anywhere
   * in the app, and WRONG for this list — a designation is per (org, person), so offering a
   * member of agency B while assigning into agency A produces a request the server correctly
   * refuses with `CRM_SCOPE_FORBIDDEN: … is not a member of this organization`.
   *
   * Reported 2026-09-01: a colleague from another agency was listed here while the caller
   * was on WEBOT, and picking them 403'd at the very last step — after choosing a copy
   * source, which is the most annoying possible moment to be told.
   *
   * `GET /crm/org/directory?orgId=` already scopes the roster server-side (it degrades to
   * "self only" for an org the caller is not in, and never throws), so the fix is to ask the
   * scoped question. One request, on open, beside the holders lookup this dialog already does.
   */
  const [orgMembers, setOrgMembers] = useState<OrgDirectoryMember[] | null>(null);
  const [selected, setSelected] = useState<string | null>(handler?.userId ?? null);
  const [copyFrom, setCopyFrom] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [holders, setHolders] = useState<CrmContactHolder[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on every open: a dialog that reopens carrying the last contact's selection is how
  // an admin designates the wrong person while believing they cancelled.
  useEffect(() => {
    if (!open) return;
    setSelected(handler?.userId ?? null);
    setCopyFrom(null);
    setSearch("");
    setError(null);
    setHolders(null);
    setOrgMembers(null);
  }, [open, contactId, handler?.userId]);

  /**
   * Who already holds this person — the per-row hints in step A and the whole of step B.
   *
   * Fetched HERE rather than threaded down from the list: the list's holders request is
   * gated on `browse` + `pool`, which an admin has and a hand-off handler may not, and this
   * dialog must work for both. A refusal degrades to `null`, which collapses step B and
   * turns the hints off — the designation itself still works, which is the important half.
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    contactsHolders({ orgId, contactIds: [contactId] })
      .then((response) => {
        if (!cancelled) setHolders(response.enabled ? response.holders[contactId] ?? [] : []);
      })
      .catch(() => {
        if (!cancelled) setHolders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId, contactId, contactsHolders]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    orgDirectory(orgId)
      .then((response) => {
        if (!cancelled) setOrgMembers(response.members ?? []);
      })
      .catch(() => {
        // An empty roster renders "no colleagues" rather than a list that would 403 on
        // click. Silence beats offering an option the server will refuse.
        if (!cancelled) setOrgMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId, orgDirectory]);

  const holderIds = useMemo(() => new Set((holders ?? []).map((holder) => normalizeCrmOwnerId(holder.ownerUserId))), [holders]);

  const roster = useMemo(() => {
    const scoped = orgMembers ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter(
      (member) => (member.displayName ?? "").toLowerCase().includes(needle) || member.userId.toLowerCase().includes(needle)
    );
  }, [orgMembers, search]);

  const assigneeHoldsIt = selected ? holderIds.has(normalizeCrmOwnerId(selected)) : false;

  /**
   * The colleagues who could be copied FROM, EARLIEST-CREATED FIRST.
   *
   * That order is the default selection, and it is the "who got the lead first" rule the
   * agency panel already marks with `ראשון`: the oldest record is the one most likely to
   * carry the original lead source and the longest history of criteria.
   */
  const otherHolders = useMemo(
    () =>
      (holders ?? [])
        .filter((holder) => !sameCrmOwner(holder.ownerUserId, selected))
        // An unknown creation date sorts LAST, not first. `createdTime` is already
        // COALESCEd server-side (`created_time_ms` → `fs_create_time_ms`), so a null here is
        // rare — but `new Date(null)` is the epoch, which would silently make the one record
        // we know least about the default source.
        .sort((a, b) => (a.createdTime ? Date.parse(a.createdTime) : Infinity) - (b.createdTime ? Date.parse(b.createdTime) : Infinity)),
    [holders, selected]
  );

  // Step B only has a QUESTION when there is more than one answer. With exactly one holder
  // the source is not a choice, so it is stated in the consequence line instead.
  const needsSourceChoice = Boolean(selected) && !assigneeHoldsIt && otherHolders.length > 1;

  /**
   * ⚠️ THE DEFAULT HAS TO BE THE SAME VALUE THE RADIO PAINTS, OR THE DIALOG LIES.
   *
   * This read `copyFrom ?? soleSource ?? null`, where `soleSource` only existed when there
   * was exactly ONE other holder. With two, the list rendered the first option as checked
   * while `effectiveSource` was still null — so the consequence line said "לא ייווצר עותק"
   * underneath a visibly selected source, and pressing the button would have designated
   * without copying anything. Reported from a screenshot, 2026-09-01.
   *
   * One expression now feeds the radio, the sentence and the request.
   */
  const effectiveSource = assigneeHoldsIt ? null : copyFrom ?? otherHolders[0]?.ownerUserId ?? null;

  const selectedName = selected ? getMember(selected)?.displayName || selected : null;
  const isClearing = selected === null && handler !== null;

  const consequence = (() => {
    if (isClearing) return "השיוך יוסר. הכרטיסים הקיימים לא ישתנו.";
    if (!selected) return null;
    if (assigneeHoldsIt) return `ל${selectedName} כבר יש כרטיס ללקוח הזה — לא ייווצר עותק חדש, רק ישויך הטיפול.`;
    if (!effectiveSource) return `לא ייווצר עותק — ${selectedName} ישויך/תשויך לטיפול בלי כרטיס משלו/ה.`;
    const sourceName = getMember(effectiveSource)?.displayName || effectiveSource;
    return `ייווצר עותק של הכרטיס של ${sourceName} אצל ${selectedName}. מרגע זה שני הכרטיסים נפרדים — שינוי באחד לא משנה את השני. היסטוריית השיחות, הפגישות והמסמכים נשארת אצל ${sourceName}.`;
  })();

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await crmAssignContactHandler({
        orgId,
        contactId,
        assigneeUserId: selected,
        copyFromOwnerUserId: assigneeHoldsIt ? null : effectiveSource
      });
      onAssigned(
        response.assigneeUserId
          ? {
              orgId: response.orgId,
              orgName: null,
              assigneeUserId: response.assigneeUserId,
              assignedAt: new Date().toISOString(),
              stale: false
            }
          : null
      );
      onClose();
    } catch (caught) {
      // The server's message names the reason and the fix — a soft-deleted record to
      // restore, an agent who does not hold the contact. Rendering it verbatim is the point;
      // "could not assign" without the reason reads as a bug.
      setError((caught as { message?: string })?.message ?? "השיוך נכשל. נסו שוב.");
    } finally {
      setSaving(false);
    }
  };

  const hintFor = (userId: string): string | null => {
    if (sameCrmOwner(userId, handler?.userId)) return "מטפל כעת";
    if (holders === null) return null;
    return holderIds.has(normalizeCrmOwnerId(userId)) ? "שמור אצלו" : "ייווצר עותק חדש";
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 0.5 }}>
        {handler ? "העברת הטיפול" : "שיוך מתווך מטפל"}
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.25 }}>
          {displayName || null}
          {displayName ? " · " : ""}
          {/* An LTR island via the `dir` ATTRIBUTE — `sx={{ direction }}` is mirrored by
              stylis-plugin-rtl into its opposite. */}
          <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
            {formatContactPhone(contactId)}
          </Box>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {handler?.stale && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {`${handler.name} אינו/ה חבר/ה במשרד יותר.`}
          </Alert>
        )}

        {(orgMembers?.length ?? 0) > 8 && (
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="חיפוש מתווך"
            sx={{ mb: 1.5 }}
          />
        )}

        {/* A FIXED, SCROLLING BOX rather than a list that grows with the agency.
            The largest agency in the fleet is 34 people, and at ~44px a row that is 1,500px
            of dialog — so the copy-source step and the consequence sentence below it fell
            off the bottom of the screen, and the primary button went with them. Capped at a
            little over five rows so it is visibly scrollable (a cap at an exact row boundary
            reads as a complete list), with the search box above it already handling the
            large-agency case. */}
        <Box sx={{ maxHeight: 248, overflowY: "auto", overscrollBehavior: "contain", mx: -1, px: 1 }}>
          {/* `null` is "the roster has not answered", `[]` is "this agency has nobody else".
              An empty box for the first reads as the second, which for an admin who has just
              opened this on a 16-person agency is alarming rather than merely wrong. */}
          {orgMembers === null && (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={20} />
            </Stack>
          )}
          <Stack spacing={0.5}>
            {roster.map((member) => {
            const hint = hintFor(member.userId);
            return (
              <Stack
                key={member.userId}
                component="label"
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ minHeight: 44, cursor: "pointer", minWidth: 0 }}
              >
                <Radio
                  size="small"
                  checked={sameCrmOwner(selected, member.userId)}
                  onChange={() => {
                    setSelected(member.userId);
                    setCopyFrom(null);
                  }}
                />
                <CrmMemberAvatar name={member.displayName ?? member.userId} avatarUrl={member.avatarUrl} size={28} />
                <Typography variant="body2" noWrap sx={{ minWidth: 0, flexGrow: 1 }}>
                  {member.displayName || member.userId}
                  {sameCrmOwner(member.userId, viewerUserId) ? " (אני)" : ""}
                </Typography>
                {hint && (
                  <Label color={hint === "מטפל כעת" ? "warning" : "default"} variant="ghost">
                    {hint}
                  </Label>
                )}
              </Stack>
              );
            })}
          </Stack>
        </Box>

        {/* Absent when nothing is designated: "remove" is not an option when there is
            nothing to remove. */}
        {handler && (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack component="label" direction="row" alignItems="center" spacing={1} sx={{ minHeight: 44, cursor: "pointer" }}>
              <Radio size="small" checked={selected === null} onChange={() => setSelected(null)} />
              <Typography variant="body2">ללא מתווך מטפל</Typography>
            </Stack>
          </>
        )}

        {needsSourceChoice && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              מאיזה כרטיס להעתיק?
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {otherHolders.map((holder, index) => (
                <Stack
                  key={holder.ownerUserId}
                  component="label"
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                  sx={{ cursor: "pointer", minWidth: 0, py: 0.5 }}
                >
                  <Radio
                    size="small"
                    checked={sameCrmOwner(effectiveSource, holder.ownerUserId)}
                    onChange={() => setCopyFrom(holder.ownerUserId)}
                  />
                  <CrmMemberAvatar
                    name={getMember(holder.ownerUserId)?.displayName || holder.ownerName || holder.ownerUserId}
                    avatarUrl={getMember(holder.ownerUserId)?.avatarUrl ?? holder.ownerAvatarUrl}
                    size={28}
                  />
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                        {getMember(holder.ownerUserId)?.displayName || holder.ownerName || holder.ownerUserId}
                      </Typography>
                      {/* The default, and WHY it is the default — the same `ראשון` mark the
                          agency panel puts on this record. Without it the pre-selected radio
                          looks arbitrary, and an admin has no reason to trust or change it. */}
                      {index === 0 && (
                        <Label color="info" variant="ghost">
                          ראשון
                        </Label>
                      )}
                    </Stack>
                    {/* `שינוי אחרון`, NEVER `פנייה אחרונה` — it moves on writes no human
                        made. The server's own comment on this field says so. */}
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                      {[holder.name ? `בשם ${holder.name}` : null, holder.createdTime ? `נוצר ${relativeHebrew(holder.createdTime)}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </>
        )}

        {consequence && (
          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            {consequence}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          ביטול
        </Button>
        <Button
          onClick={submit}
          variant={isClearing ? "outlined" : "contained"}
          color={isClearing ? "error" : "primary"}
          disabled={saving || (selected === null && handler === null)}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isClearing ? "בטל שיוך" : assigneeHoldsIt || !effectiveSource ? "שייך" : "שייך והעתק כרטיס"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
